// src/pages/CuentasCobro.js
//
// D-24 — Cuentas de cobro de la suscripción del negocio (HolaVeci): mes,
// tipo, valor, estado, fechas y PDF. Pago de la pendiente si la hay.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Tooltip, LinearProgress, Alert, Stack, Button, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { mensualidad as apiMensualidad } from '../api/holaveciLocalApi';
import { useApp } from '../context/AppContext';
import PagoLocalWompi from '../components/holaveciLocal/PagoLocalWompi';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const TIPOS = { MENSUALIDAD: 'Mensualidad', PRIMER_MES: 'Primer mes', AJUSTE_360: 'Paso a HolaVeci 360°' };
const cop = (n) => (n ?? 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric' });
};
const chip = (f) => {
  if (f.estado === 'PAGADA') return <Chip size="small" color="success" label="Pagada" />;
  if (f.vencida) return <Chip size="small" color="error" label="Vencida" />;
  return <Chip size="small" color="warning" label="Pendiente" />;
};

export default function CuentasCobro() {
  const { notify } = useApp();
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [pagar, setPagar] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try { setItems((await apiMensualidad.cuentasCobro()).items || []); }
    catch (e) { notify(e.mensajeUsuario || 'No se pudieron cargar tus cuentas de cobro.', 'error'); }
    finally { setCargando(false); }
  }, [notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const hayPendiente = items.some(i => i.estado !== 'PAGADA');

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 34 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">Mis cuentas de cobro</Typography>
          <Typography variant="body2" color="text.secondary">
            Tu suscripción a HolaVeci se cobra el día 1 de cada mes.
          </Typography>
        </Box>
        {hayPendiente && <Button variant="contained" onClick={() => setPagar(true)}>Pagar pendiente</Button>}
      </Stack>

      <Card>
        {cargando && <LinearProgress />}
        <CardContent sx={{ p: 0 }}>
          {!cargando && items.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>Aún no tienes cuentas de cobro.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Pagada / límite</TableCell>
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(f => (
                  <TableRow key={f.facturaId} hover>
                    <TableCell>{f.numeroFactura}</TableCell>
                    <TableCell>{MESES[(f.periodoMes || 1) - 1]} {f.periodoAnio}</TableCell>
                    <TableCell>{TIPOS[f.tipo] || f.tipo}</TableCell>
                    <TableCell align="right"><b>{cop(f.total)}</b></TableCell>
                    <TableCell>{chip(f)}</TableCell>
                    <TableCell>{f.estado === 'PAGADA' ? fecha(f.fechaPago) : fecha(f.fechaLimitePago)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver PDF">
                        <IconButton size="small" onClick={() => apiMensualidad.abrirPdf(f.facturaId).catch(e => notify(e.message, 'error'))}>
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={pagar} onClose={() => setPagar(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pagar mensualidad</DialogTitle>
        <DialogContent dividers>
          <PagoLocalWompi modo="mensualidad" onPagado={() => { setPagar(false); notify('Pago confirmado. ¡Gracias!', 'success'); cargar(); }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setPagar(false)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
